(() => {
  'use strict';

  const ELEMENT_NAME = 'mz-model-finder';
  const FILTER_KEYS = ['make', 'model', 'year', 'classification'];
  const GENERATED_OPTION_KEYS = [...FILTER_KEYS];
  const MOBILE_QUERY = '(max-width: 749px)';

  const normalize = (value) => {
    const text = String(value == null ? '' : value).trim().toLocaleLowerCase();

    try {
      return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (_error) {
      return text;
    }
  };

  const isEnabled = (element, attributeName) => {
    if (!element.hasAttribute(attributeName)) return false;

    const value = normalize(element.getAttribute(attributeName));
    return !['false', '0', 'off', 'no'].includes(value);
  };

  const canonicalValue = (value) => {
    const cleanValue = String(value == null ? '' : value).trim();
    return normalize(cleanValue) === 'all' ? '' : cleanValue;
  };

  class MotoZeroModelFinder extends HTMLElement {
    constructor() {
      super();
      this._ready = false;
      this._cleanups = [];
      this._stickyFrame = null;
      this._stickyVisible = false;
      this._stickyHeight = 0;
      this.state = Object.fromEntries(FILTER_KEYS.map((key) => [key, '']));
    }

    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.cards = Array.from(this.querySelectorAll('[data-model-card]'));
      this.controls = Array.from(this.querySelectorAll('[data-filter-key]')).filter((control) =>
        FILTER_KEYS.includes(control.dataset.filterKey)
      );
      this.filterPanel = this.querySelector('[data-filter-panel]');
      this.stickyFilter = this.querySelector('[data-sticky-filter]');
      this.emptyStates = Array.from(this.querySelectorAll('[data-filter-empty]'));
      this.resultElements = Array.from(
        this.querySelectorAll('[data-filter-result], [data-filter-results]')
      );
      this.countElements = Array.from(this.querySelectorAll('[data-filter-count]'));
      this.models = this.buildModels();

      this.populateSelects();
      this.readInitialState();
      this.configureSingleValueFields();
      this.syncControls();
      this.bindControls();
      this.bindStickyFilter();
      this.bindThemeEditor();
      this.applyFilters();

      // Liquid keeps the panel hidden until its generated controls are usable.
      if (this.filterPanel) this.filterPanel.hidden = false;
      this.scheduleStickyUpdate();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      this._cleanups.splice(0).forEach((cleanup) => cleanup());

      if (this._stickyFrame != null) {
        cancelAnimationFrame(this._stickyFrame);
        this._stickyFrame = null;
      }

      this.setStickyVisibility(false);
      this._ready = false;
      this.cards = [];
      this.controls = [];
      this.models = [];
    }

    listen(target, eventName, handler, options) {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      this._cleanups.push(() => target.removeEventListener(eventName, handler, options));
    }

    buildModels() {
      return this.cards
        .map((card, domIndex) => {
          const position = Number.parseInt(card.dataset.position, 10);
          const make = this.splitValues(card.dataset.make);
          const model = this.splitValues(card.dataset.model);
          const year = this.splitValues(card.dataset.year);
          const classification = this.splitValues(card.dataset.classification);

          return {
            card,
            domIndex,
            position: Number.isFinite(position) ? position : domIndex,
            make,
            model,
            year,
            classification,
          };
        })
        .sort((first, second) => first.position - second.position || first.domIndex - second.domIndex);
    }

    splitValues(value) {
      const source = String(value || '').trim();
      if (!source) return [];

      if (source.startsWith('[')) {
        try {
          const parsed = JSON.parse(source);
          if (Array.isArray(parsed)) {
            return parsed.map((item) => String(item).trim()).filter(Boolean);
          }
        } catch (_error) {
          // Fall through to the compact pipe-delimited metafield format.
        }
      }

      return source
        .split(/\s*\|\|?\s*/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    uniqueValues(key, models = this.models) {
      const seen = new Map();

      models.forEach((model) => {
        const values = model[key];
        (Array.isArray(values) ? values : []).forEach((value) => {
          const normalizedValue = normalize(value);
          if (normalizedValue && !seen.has(normalizedValue)) seen.set(normalizedValue, value);
        });
      });

      const language = document.documentElement.lang || undefined;
      return Array.from(seen.values()).sort((first, second) => {
        const comparison = first.localeCompare(second, language, { numeric: true, sensitivity: 'base' });
        return key === 'year' ? comparison * -1 : comparison;
      });
    }

    modelsForFacet(excludedKey) {
      return this.models.filter((model) =>
        FILTER_KEYS.every(
          (key) => key === excludedKey || this.matchesList(model[key], this.state[key])
        )
      );
    }

    populateSelects() {
      GENERATED_OPTION_KEYS.forEach((key) => {
        const values = this.uniqueValues(key, this.modelsForFacet(key));
        const selects = this.controls.filter(
          (control) => control.dataset.filterKey === key && control.tagName === 'SELECT'
        );

        selects.forEach((select) => {
          const allOption = Array.from(select.options).find((option) => {
            const optionValue = normalize(option.value);
            return !optionValue || optionValue === 'all';
          });
          const allLabel =
            select.dataset.allLabel || allOption?.textContent?.trim() || select.getAttribute('aria-label') || 'Alle';
          const fragment = document.createDocumentFragment();

          fragment.append(new Option(allLabel, ''));
          values.forEach((value) => fragment.append(new Option(value, value)));
          select.replaceChildren(fragment);
          select.value = values.some((value) => normalize(value) === normalize(this.state[key]))
            ? this.state[key]
            : '';
        });
      });
    }

    reconcileState(changedKey) {
      const resetOrderByKey = {
        make: ['model', 'year', 'classification'],
        model: ['make', 'year', 'classification'],
        year: ['model', 'make', 'classification'],
        classification: ['make', 'model', 'year'],
      };
      const resetOrder = resetOrderByKey[changedKey] || FILTER_KEYS.filter((key) => key !== changedKey);

      resetOrder.forEach((key) => {
        if (!this.state[key]) return;
        const hasMatch = this.models.some((model) =>
          FILTER_KEYS.every((filterKey) => this.matchesList(model[filterKey], this.state[filterKey]))
        );
        if (!hasMatch) this.state[key] = '';
      });
    }

    readInitialState() {
      FILTER_KEYS.forEach((key) => {
        const keyedControls = this.controls.filter((control) => control.dataset.filterKey === key);
        const primaryControls = keyedControls.filter((control) => !control.closest('[data-sticky-filter]'));
        const candidates = primaryControls.length ? primaryControls : keyedControls;
        let value = '';

        for (const control of candidates) {
          const controlValue = this.readControl(control);
          if (controlValue !== null) {
            value = controlValue;
            break;
          }
        }

        this.state[key] = canonicalValue(value);
      });
    }

    configureSingleValueFields() {
      if (!isEnabled(this, 'data-hide-single')) return;

      GENERATED_OPTION_KEYS.forEach((key) => {
        const shouldHide = this.uniqueValues(key).length <= 1;
        const fields = new Set();

        this.controls
          .filter((control) => control.dataset.filterKey === key)
          .forEach((control) => {
            const field = control.closest('[data-filter-field]');
            if (field) fields.add(field);
            else control.hidden = shouldHide;
          });

        fields.forEach((field) => {
          field.hidden = shouldHide;
          field.setAttribute('aria-hidden', String(shouldHide));
        });

        if (shouldHide) this.state[key] = '';
      });
    }

    bindControls() {
      this.controls.forEach((control) => {
        const isButton = control.tagName === 'BUTTON';
        const eventName = isButton ? 'click' : 'change';

        this.listen(control, eventName, (event) => {
          if (isButton) event.preventDefault();
          if (control.type === 'radio' && !control.checked) return;

          const key = control.dataset.filterKey;
          const value = this.readControl(control);
          if (value === null) return;

          this.state[key] = canonicalValue(value);
          this.reconcileState(key);
          this.populateSelects();
          this.syncControls();
          this.applyFilters();
        });
      });

      this.querySelectorAll('[data-filter-reset]').forEach((button) => {
        this.listen(button, 'click', (event) => {
          event.preventDefault();
          this.resetFilters();
        });
      });

      this.querySelectorAll('form[data-filter-form]').forEach((form) => {
        this.listen(form, 'submit', (event) => event.preventDefault());
      });
    }

    readControl(control) {
      if (control.tagName === 'BUTTON') {
        return control.dataset.filterValue ?? control.value ?? '';
      }

      if (control.type === 'radio') return control.checked ? control.value : null;
      if (control.type === 'checkbox') return control.checked ? control.value || 'true' : '';
      return control.value ?? '';
    }

    syncControls(onlyKey, sourceControl) {
      const keys = onlyKey ? [onlyKey] : FILTER_KEYS;

      keys.forEach((key) => {
        const stateValue = this.state[key];
        const normalizedState = normalize(stateValue);

        this.controls
          .filter((control) => control.dataset.filterKey === key)
          .forEach((control) => {
            // Native inputs already reflect the user's edit; buttons still need their pressed state.
            if (control === sourceControl && control.tagName !== 'BUTTON') return;

            if (control.tagName === 'BUTTON') {
              const buttonValue = canonicalValue(control.dataset.filterValue ?? control.value ?? '');
              const active = normalize(buttonValue) === normalizedState;
              control.setAttribute('aria-pressed', String(active));
              control.classList.toggle('is-active', active);
              return;
            }

            if (control.type === 'radio') {
              control.checked = normalize(canonicalValue(control.value)) === normalizedState;
            } else if (control.type === 'checkbox') {
              control.checked = normalize(canonicalValue(control.value || 'true')) === normalizedState;
            } else if (control.tagName === 'SELECT') {
              const hasValue = Array.from(control.options).some(
                (option) => normalize(option.value) === normalizedState
              );
              control.value = hasValue ? stateValue : '';
            } else {
              control.value = stateValue;
            }
          });
      });
    }

    matchesList(values, selectedValue) {
      const selected = normalize(selectedValue);
      if (!selected) return true;
      return values.some((value) => normalize(value) === selected);
    }

    applyFilters() {
      let resultCount = 0;

      this.models.forEach((model) => {
        const matches =
          this.matchesList(model.make, this.state.make) &&
          this.matchesList(model.model, this.state.model) &&
          this.matchesList(model.year, this.state.year) &&
          this.matchesList(model.classification, this.state.classification);

        model.card.hidden = !matches;
        model.card.classList.toggle('is-filtered-out', !matches);
        model.card.dataset.filterMatch = String(matches);
        if (matches) resultCount += 1;
      });

      this.updateResultStatus(resultCount);
      this.emptyStates.forEach((emptyState) => {
        emptyState.hidden = resultCount !== 0;
        emptyState.setAttribute('aria-hidden', String(resultCount !== 0));
      });

      this.scheduleStickyUpdate();
    }

    updateResultStatus(count) {
      const template =
        count === 1
          ? this.dataset.resultOne || '[count] Modell gefunden'
          : this.dataset.resultMany || '[count] Modelle gefunden';
      const message = template.replace(/\[count\]/g, String(count));

      this.resultElements.forEach((resultElement) => {
        resultElement.setAttribute('role', 'status');
        resultElement.setAttribute('aria-live', 'polite');
        resultElement.setAttribute('aria-atomic', 'true');
        resultElement.textContent = message;
      });

      this.countElements.forEach((countElement) => {
        countElement.textContent = message;
      });
    }

    resetFilters() {
      FILTER_KEYS.forEach((key) => {
        this.state[key] = '';
      });
      this.populateSelects();
      this.syncControls();
      this.applyFilters();
    }

    bindStickyFilter() {
      if (!this.stickyFilter || !this.filterPanel) return;

      this.setStickyVisibility(false);
      const schedule = () => this.scheduleStickyUpdate();
      this.listen(window, 'scroll', schedule, { passive: true });
      this.listen(window, 'resize', schedule, { passive: true });

      if (window.visualViewport) {
        this.listen(window.visualViewport, 'resize', schedule, { passive: true });
        this.listen(window.visualViewport, 'scroll', schedule, { passive: true });
      }
    }

    scheduleStickyUpdate() {
      if (!this.stickyFilter || !this.filterPanel || this._stickyFrame != null) return;

      this._stickyFrame = requestAnimationFrame(() => {
        this._stickyFrame = null;
        this.updateStickyFilter();
      });
    }

    stickyTopOffset() {
      const stickyStyles = getComputedStyle(this.stickyFilter);
      const resolvedTop = Number.parseFloat(stickyStyles.top);
      if (Number.isFinite(resolvedTop)) return Math.max(0, resolvedTop);

      const customProperties = [
        '--mz-model-finder-sticky-top',
        '--mz-sticky-filter-top',
        '--mz-sticky-top',
      ];

      for (const property of customProperties) {
        const value =
          stickyStyles.getPropertyValue(property) || getComputedStyle(this).getPropertyValue(property);
        const parsedValue = Number.parseFloat(value);
        if (Number.isFinite(parsedValue)) return Math.max(0, parsedValue);
      }

      return 0;
    }

    updateStickyFilter() {
      const stickyEnabled = isEnabled(this, 'data-sticky-enabled');
      const mobileAllowed = isEnabled(this, 'data-sticky-mobile');
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;

      if (!stickyEnabled || (isMobile && !mobileAllowed)) {
        this.setStickyVisibility(false);
        return;
      }

      const topOffset = this.stickyTopOffset();
      const panelRect = this.filterPanel.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      if (this._stickyVisible) {
        this._stickyHeight = this.stickyFilter.getBoundingClientRect().height || this._stickyHeight;
      }
      const stickyHeight = this._stickyHeight || this.stickyFilter.scrollHeight || 1;
      const panelHasPassed = panelRect.bottom <= topOffset + 1;
      const sectionIsVisible =
        sectionRect.top < viewportHeight && sectionRect.bottom > topOffset + stickyHeight;

      this.setStickyVisibility(panelHasPassed && sectionIsVisible);
    }

    setStickyVisibility(visible) {
      if (!this.stickyFilter) return;
      if (visible === this._stickyVisible && this.stickyFilter.hidden === !visible) return;

      this._stickyVisible = visible;
      this.stickyFilter.hidden = !visible;
      this.stickyFilter.classList.toggle('is-visible', visible);
      this.stickyFilter.dataset.visible = String(visible);
      this.stickyFilter.setAttribute('aria-hidden', String(!visible));
      this.stickyFilter.toggleAttribute('inert', !visible);
      this.toggleAttribute('data-sticky-active', visible);

      if (visible) {
        this._stickyHeight = this.stickyFilter.getBoundingClientRect().height || this._stickyHeight;
      }
    }

    bindThemeEditor() {
      this.listen(document, 'shopify:block:select', (event) => {
        const eventTarget = event.target instanceof Element ? event.target : null;
        let selectedCard = eventTarget?.closest('[data-model-card]');

        if (!selectedCard || !this.contains(selectedCard)) {
          const blockId = String(event.detail?.blockId || '');
          selectedCard = this.cards.find((card) => {
            if (!blockId) return false;
            if (card.dataset.blockId === blockId) return true;
            if (card.getAttribute('data-shopify-editor-block') === blockId) return true;
            return Array.from(card.querySelectorAll('[data-shopify-editor-block]')).some(
              (element) => element.getAttribute('data-shopify-editor-block') === blockId
            );
          });
        }

        if (!selectedCard || !this.contains(selectedCard)) return;
        this.resetFilters();
        selectedCard.hidden = false;
      });
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, MotoZeroModelFinder);
  }
})();
